import React, { useEffect, useState } from "react";
import { Dialog, DialogBody, DialogDescription, DialogTitle } from "@components/Catalyst/dialog";
import { TabNav, TabNavItem, TabNavSection } from "@components/Catalyst/tab-nav";

import LoginForm from "@components/Forms/LoginForm";
import SmallRegisterForm from "@components/Forms/RegisterForm/small";
import SmallForgotPasswordForm from "@components/Forms/ForgotPasswordForm/small";
import SmallResetPasswordForm from "@components/Forms/ResetPasswordForm/small";
import SmallConfirmRegistrationForm from "@components/Forms/ConfirmRegistration/small";
import { Button } from "@components/Catalyst/button";
import toast from "react-hot-toast";
import { authBus } from "@contexts/AuthContext/AuthEvents";
import { useAuth } from "@contexts/AuthContext";
import { logout } from "@contexts/AuthContext/functions/logout";

const LoginModal = ({
  isOpen,
  setIsOpen,
  focusTab = "login",
  setFocusTab,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  focusTab: "login" | "new-account" | "forgot-password";
  setFocusTab: (value: "login" | "new-account" | "forgot-password") => void;
}) => {
  const { isloggedIn, email } = useAuth();

  const [newUserStep, setNewUserStep] = useState("data-entry");
  const [loginEmail, setLoginEmail] = useState("");

  const [forotPassStep, setForgotPassStep] = useState("collect-email");

  useEffect(() => {
    const unsubscribe = authBus.subscribe((event) => {
      switch (event.type) {
        case "transition":
          if (event.email) setLoginEmail(event.email);

          switch (event.state) {
            case "login":
              setFocusTab("login");
              setNewUserStep("data-entry");
              setForgotPassStep("collect-email");
              break;
            case "get-email-confirmation":
              setFocusTab("new-account");
              setNewUserStep("confirm-email");
              break;
            case "get-reset-code":
              setFocusTab("forgot-password");
              setForgotPassStep("reset-pass");
              break;
            case "authenticated":
              setIsOpen(false);
              setNewUserStep("data-entry");
              setForgotPassStep("collect-email");
              break;
            default:
          }
          break;
        case "message":
          if (event.level === "error") toast.error(event.text, { id: "auth-message" });
          else toast.success(event.text, { id: "auth-message" });
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, []);

  if (isloggedIn) {
    return (
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle className="flex items-center gap-2">
          <img className="w-7" src="/images/logos/Stud_grn.svg" alt="Studio Assistant" />
          Studio Assistant
        </DialogTitle>
        <DialogBody>
          <div className="flex flex-col gap-4">
            Signed in as {email}
            <div>
              <Button onClick={() => logout()}>Sign Out</Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle className="flex items-center gap-2">
        <img className="w-7" src="/images/logos/Stud_grn.svg" alt="Studio Assistant" />
        Studio Assistant
      </DialogTitle>
      <DialogDescription>
        Studio Assistant provides you with full access to all the digital tools offered by Golden Artist Colors.
      </DialogDescription>
      <DialogBody>
        <TabNav>
          <TabNavSection>
            <TabNavItem current={focusTab === "login"} onClick={() => setFocusTab("login")}>
              Sign In
            </TabNavItem>
            <TabNavItem current={focusTab === "new-account"} onClick={() => setFocusTab("new-account")}>
              Create New Account
            </TabNavItem>
            <TabNavItem current={focusTab === "forgot-password"} onClick={() => setFocusTab("forgot-password")}>
              Forgot Password
            </TabNavItem>
          </TabNavSection>
        </TabNav>

        <div className="flex flex-col space-y-4 pl-2 pt-6">
          {focusTab === "login" && <LoginForm email={loginEmail} setEmail={setLoginEmail} />}
          {focusTab === "new-account" && newUserStep === "data-entry" && (
            <>
              <SmallRegisterForm />

              <Button plain onClick={() => setNewUserStep("confirm-email")}>
                <div className="flex items-center text-sm text-cyan-700 underline">Already have a code? Click to enter here.</div>
              </Button>
            </>
          )}
          {focusTab === "new-account" && newUserStep === "confirm-email" && (
            <>
              <SmallConfirmRegistrationForm email={loginEmail} />

              <Button plain onClick={() => setNewUserStep("data-entry")}>
                <div className="flex items-center text-sm text-cyan-700 underline">Create an account.</div>
              </Button>
            </>
          )}

          {focusTab === "forgot-password" && forotPassStep === "collect-email" && <SmallForgotPasswordForm email={loginEmail} />}
          {focusTab === "forgot-password" && forotPassStep === "reset-pass" && <SmallResetPasswordForm email={loginEmail} />}
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default LoginModal;
